import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { differenceInDays, isAfter, parseISO, startOfDay, subDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { getAssortedFamilies } from '../../redux/actions/resellerActionCreators';
import { ClaimCategory, ClaimType, RootState } from '../../redux/types';
import {
    GenerateClaimTemplateInput,
    GenerateClaimTemplateOutput,
    KIDS_TO_OPS_FAMILY_MAPPING,
    PROMOTION_FILTER_MODE,
} from '../../types';
import { useTranslation, WaitFor } from '../blocks';
import { useModal } from './Modal';
import { usePageMessaging } from './PageMessaging';
import { ProgressLoader } from './ProgressLoader';

type GeneratePreFilledClaimTemplatePopupProps = {
    resellerId: string;
};

const MAX_DATE_RANGE_DAYS = 121; // Max date range agreed by Sales
const MAX_DATE_RANGE_MONTHS = 4;
const INCREMENT_PER_DAY_IN_SECONDS = 0.15; // wait time increment in seconds for each additional day
const MIN_WAIT_TIME_SECONDS = 8;
// end date buffer that determine the end date the user is able to select for claims. If zero that would be current day.
const END_DATE_BUFFER_LIMIT_DAYS = 1;
const ACCESSORY_FAMILY = 'Accessory';

const GeneratePreFilledClaimTemplatePopup = ({
    resellerId,
    assortedFamilies,
    getAssortedFamilies,
}: GeneratePreFilledClaimTemplatePopupProps & GeneratePreFilledClaimTemplatePopupReduxProps) => {
    const {
        claims: { generateClaimTemplate },
    } = useHrpsApi();
    const { t } = useTranslation('preFilledClaimTemplate');
    const { t: tCommon } = useTranslation('common');
    const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
    const [productFamilies, setProductFamilies] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [dateError, setDateError] = useState('');
    const [informationMessage, setInformationMessage] = useState('');
    const [isLoaderVisible, setIsLoaderVisible] = useState(false);
    const [templateUrl, setTemplateUrl] = useState('');
    const [promotionFilterMode, setPromotionFilterMode] = useState(PROMOTION_FILTER_MODE.ACTIVE_IN_RANGE);

    const modal = useModal();
    const pageMessaging = usePageMessaging();

    useEffect(() => {
        if (assortedFamilies.status === 'Uninitialized') {
            getAssortedFamilies(resellerId);
        } else if (assortedFamilies.status === 'Failed') {
            pageMessaging.showError(t('getAssortedFamilies-error'));
            modal.close();
        }
    }, [assortedFamilies.status, getAssortedFamilies, resellerId, pageMessaging, t, modal]);

    const familyMappings = useMemo(() => {
        const families = assortedFamilies.value || [];
        const accessoryMapping = { kidsFamily: ACCESSORY_FAMILY, ...KIDS_TO_OPS_FAMILY_MAPPING[ACCESSORY_FAMILY] };
        const assortedMappings = families
            .filter((kidsFamily) => KIDS_TO_OPS_FAMILY_MAPPING[kidsFamily])
            .map((kidsFamily) => ({
                kidsFamily,
                ...KIDS_TO_OPS_FAMILY_MAPPING[kidsFamily],
            }));
        return [accessoryMapping, ...assortedMappings];
    }, [assortedFamilies.value]);

    const getFamilySelectOptions = useMemo(() => {
        return familyMappings.map((mapping) => (
            <SelectOption key={mapping.kidsFamily} value={mapping.kidsFamily} label={mapping.displayName} />
        ));
    }, [familyMappings]);

    // simple estimation for the time it takes to generate a pre-filled claim template
    const getEstimatedTime = () => {
        const diffDays = differenceInDays(parseISO(dateRange[1]), parseISO(dateRange[0]));
        return Math.round(
            MIN_WAIT_TIME_SECONDS + Math.min(diffDays, MAX_DATE_RANGE_DAYS) * INCREMENT_PER_DAY_IN_SECONDS
        );
    };

    const isDisabledDate = (date: string) => {
        const maxDate = startOfDay(subDays(new Date(), END_DATE_BUFFER_LIMIT_DAYS));
        const inputDate = startOfDay(parseISO(date));
        return isAfter(inputDate, maxDate);
    };

    const isValidateDateRange = () => {
        if (!dateRange[0] || !dateRange[1]) {
            setStartDateError(true);
            setEndDateError(true);
            setDateError(t('datepicker-noSelectedDateRange-error'));
            return false;
        }
        const daysDiff = differenceInDays(parseISO(dateRange[1]), parseISO(dateRange[0]));

        if (daysDiff > MAX_DATE_RANGE_DAYS) {
            setDateError(t('datepicker-maxDateRange-error', { limit: MAX_DATE_RANGE_MONTHS }));
            setStartDateError(true);
            setEndDateError(true);
            return false;
        }

        setStartDateError(false);
        setEndDateError(false);
        setDateError('');
        return true;
    };

    const getPayload = (): GenerateClaimTemplateInput => {
        const opsFamiliesToFilterBy =
            productFamilies.length > 0
                ? productFamilies.flatMap((kidsFamily) => {
                      const mapping = familyMappings.find((m) => m.kidsFamily === kidsFamily);
                      return mapping?.opsFamilies || [];
                  })
                : familyMappings.flatMap((m) => m.opsFamilies);

        return {
            claimCategory: ClaimCategory.PROMOTIONAL,
            claimType: ClaimType.TPR,
            preFilled: true,
            resellerId: resellerId,
            productFamilies: opsFamiliesToFilterBy,
            filterMode: promotionFilterMode,
            dateRange: {
                fromDate: new Date(`${dateRange[0]}T00:00:00Z`).getTime() / 1000,
                toDate: new Date(`${dateRange[1]}T00:00:00Z`).getTime() / 1000,
            },
        };
    };

    const generatePreFilledTemplate = async () => {
        if (!isValidateDateRange()) return;

        setIsLoaderVisible(true);
        setIsLoading(true);
        setError('');
        setInformationMessage('');
        setTemplateUrl('');
        const payload = getPayload();
        try {
            const result: GenerateClaimTemplateOutput = await generateClaimTemplate(payload);

            if (result?.claimUploadTemplate?.url) {
                setTemplateUrl(result.claimUploadTemplate.url);
            } else {
                setInformationMessage(t('noPromotionsFound-informational'));
            }
        } catch (error) {
            setError(t('failedToGenerate-error'));
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = useCallback(() => {
        modal.close();
    }, [modal]);

    const onFinished = useCallback(() => {
        setIsLoaderVisible(false);
        if (templateUrl) {
            const newWindow = window.open(templateUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                // Popup was blocked, keep modal open so user can try again or copy URL
                setError(t('popup-blocked-error'));
            } else {
                closeModal();
            }
        }
    }, [closeModal, t, templateUrl]);

    return (
        <WaitFor lazyValue={assortedFamilies} showSpinner={true}>
            {() => (
                <Column spacing={'300'} alignmentVertical={'justify'}>
                    <Row spacing={'400'} spacingInset={'300'}>
                        <Column spacing={'400'}>
                            <p>{t('card-description')}</p>
                            <DateRangePicker
                                disabled={isLoading}
                                value={dateRange}
                                onChange={setDateRange}
                                monthsInView={2}
                                disabledDates={isDisabledDate}
                                clearButton={!isLoading}
                                size={'medium'}
                                startError={startDateError}
                                endError={endDateError}
                                errorMessage={dateError}
                                helperText={t('datepicker-helperText')}
                                allowSameStartAndEnd={true}
                                startLabel={tCommon('dateRangePicker-startPlaceholder')}
                                endLabel={tCommon('dateRangePicker-endPlaceholder')}
                            />
                            <Column spacing={'100'}>
                                <Text type={'b300'} color={'primary'}>
                                    {t('claimPromotionsFilterMode-instructions')}
                                </Text>
                                <RadioButton
                                    checked={promotionFilterMode === PROMOTION_FILTER_MODE.ACTIVE_IN_RANGE}
                                    onChange={setPromotionFilterMode}
                                    value={PROMOTION_FILTER_MODE.ACTIVE_IN_RANGE}
                                >
                                    {t('activeInRange-text')}
                                    <Text type={'b200'} color={'secondary'}>
                                        {t('activeInRange-description')}
                                    </Text>
                                </RadioButton>
                                <RadioButton
                                    checked={promotionFilterMode === PROMOTION_FILTER_MODE.STARTS_IN_RANGE}
                                    onChange={setPromotionFilterMode}
                                    value={PROMOTION_FILTER_MODE.STARTS_IN_RANGE}
                                >
                                    {t('startedInRange-text')}
                                    <Text type={'b200'} color={'secondary'}>
                                        {t('startedInRange-description')}
                                    </Text>
                                </RadioButton>
                            </Column>
                            <Select
                                disabled={isLoading}
                                value={productFamilies}
                                onChange={setProductFamilies}
                                helperText={t('select-productFamily-helperText')}
                                label={
                                    productFamilies.length === 0
                                        ? t('allFamilies-text')
                                        : t('select-productFamily-placeholder')
                                }
                                data-testid={'product-family-select'}
                                size={'medium'}
                            >
                                {getFamilySelectOptions}
                            </Select>
                        </Column>
                    </Row>

                    <Row alignmentHorizontal={'center'} spacingInset={'100'}>
                        {!isLoaderVisible && informationMessage && (
                            <Alert size={'large'} type={'informational'}>
                                {informationMessage}
                            </Alert>
                        )}
                        {!isLoaderVisible && error && (
                            <Alert size={'large'} type={'error'}>
                                {error}
                            </Alert>
                        )}
                    </Row>

                    {isLoaderVisible ? (
                        <ProgressLoader
                            isLoading={isLoading}
                            estimatedTime={getEstimatedTime()}
                            onFinished={onFinished}
                            infoMessage={t('waitForTemplate-informationalText')}
                        />
                    ) : (
                        <Row
                            spacing={'400'}
                            alignmentHorizontal={'center'}
                            widths={['25%', '25%']}
                            spacingInset={'200'}
                        >
                            <Button disabled={isLoading} onClick={closeModal} type={'secondary'} size={'large'}>
                                {tCommon('cancel-text')}
                            </Button>
                            <Button
                                disabled={isLoading}
                                onClick={generatePreFilledTemplate}
                                type={'primary'}
                                size={'large'}
                            >
                                {tCommon('download-text')}
                            </Button>
                        </Row>
                    )}
                </Column>
            )}
        </WaitFor>
    );
};

const mapStateToProps = ({ resellerReducer }: RootState) => ({
    assortedFamilies: resellerReducer.assortedFamilies,
});

const mapDispatchToProps = {
    getAssortedFamilies,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type GeneratePreFilledClaimTemplatePopupReduxProps = ConnectedProps<typeof connector>;

export default connector(GeneratePreFilledClaimTemplatePopup);
